import { useEffect, useRef, useState } from "react";
import { requestDailyStats } from "@/features/analytics/api";
import { REQUEST_CANCEL_VISIBILITY_DELAY_MS } from "@/features/analytics/constants";
import type {
  AnalyticsDailyStatsRow,
  AnalyticsDateRange,
  AnalyticsFilters,
} from "@/features/analytics/types";
import { createDailyStatsRequestForRange } from "@/features/analytics/utils/analytics";

type UseAnalyticsReportParams = {
  selectedRange: AnalyticsDateRange;
  appliedFilters: AnalyticsFilters;
};

type UseAnalyticsReportResult = {
  rows: AnalyticsDailyStatsRow[];
  isLoading: boolean;
  isRequestCancelable: boolean;
  isRunReportButtonDisabled: boolean;
  runReportButtonLabel: string;
  errorMessage: string;
  handleRunReportButtonClick: () => void;
};

export const useAnalyticsReport = ({
  selectedRange,
  appliedFilters,
}: UseAnalyticsReportParams): UseAnalyticsReportResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRequestCancelable, setIsRequestCancelable] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [rows, setRows] = useState<AnalyticsDailyStatsRow[]>([]);
  const requestAbortControllerRef = useRef<AbortController | null>(null);
  const cancelVisibilityTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (cancelVisibilityTimeoutRef.current !== null) {
        window.clearTimeout(cancelVisibilityTimeoutRef.current);
      }

      requestAbortControllerRef.current?.abort();
    };
  }, []);

  const clearCancelVisibilityTimeout = (): void => {
    if (cancelVisibilityTimeoutRef.current !== null) {
      window.clearTimeout(cancelVisibilityTimeoutRef.current);
      cancelVisibilityTimeoutRef.current = null;
    }
  };

  const handleRunReport = async (): Promise<void> => {
    const abortController = new AbortController();

    requestAbortControllerRef.current = abortController;
    setIsLoading(true);
    setIsRequestCancelable(false);
    setErrorMessage("");

    cancelVisibilityTimeoutRef.current = window.setTimeout(() => {
      if (requestAbortControllerRef.current === abortController) {
        setIsRequestCancelable(true);
      }
    }, REQUEST_CANCEL_VISIBILITY_DELAY_MS);

    try {
      const payload = createDailyStatsRequestForRange(selectedRange, appliedFilters);
      const response = await requestDailyStats(payload, {
        signal: abortController.signal,
      });

      setRows(response);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }

      setRows([]);
      setErrorMessage("Request failed. Check browser console and network tab.");
      console.error("statistics/daily-stats request failed");
    } finally {
      clearCancelVisibilityTimeout();

      if (requestAbortControllerRef.current === abortController) {
        requestAbortControllerRef.current = null;
        setIsLoading(false);
        setIsRequestCancelable(false);
      }
    }
  };

  const handleCancelRequest = (): void => {
    clearCancelVisibilityTimeout();
    requestAbortControllerRef.current?.abort();
    requestAbortControllerRef.current = null;
    setIsLoading(false);
    setIsRequestCancelable(false);
  };

  const handleRunReportButtonClick = (): void => {
    if (isLoading && isRequestCancelable) {
      handleCancelRequest();
      return;
    }

    if (isLoading) {
      return;
    }

    void handleRunReport();
  };

  return {
    rows,
    isLoading,
    isRequestCancelable,
    isRunReportButtonDisabled: isLoading && !isRequestCancelable,
    runReportButtonLabel: isLoading && isRequestCancelable
      ? "Cancel request"
      : "Run report",
    errorMessage,
    handleRunReportButtonClick,
  };
};
